/**
 * Costs (roadmap F2 and F4): what the organisation's AI work cost, rolled up from the usage
 * workbenches and nodes report, by business area, project, team, person, model or machine,
 * over time; and a monthly chargeback statement per business area that reconciles to the
 * usage ledger exactly.
 *
 *   GET /admin/v1/costs?since=&until=&by=area|project|team|person|model|device&bucket=day|week|month&billing=metered|subscription|all
 *       administrators and portfolio managers: totals, the same period before, and one line per group with its series
 *       (read from the daily rollups, so periods count whole UTC days)
 *   GET /admin/v1/chargeback?month=YYYY-MM[&format=csv]
 *       the statement for a month: one line per business area with its projects, and the unallocated remainder;
 *       `format=csv` (or Accept: text/csv) answers the statement as CSV for finance systems;
 *       a closed month answers its frozen statement (`closed: true`)
 *   POST /admin/v1/chargeback/close { month }
 *       administrators: freeze a month's statement as it stands, at that moment's money rate; usage for the
 *       month that arrives later is shown in the next month's `lateAdjustments`
 *
 * Cost round 2 (AIOE 0.18.0): a statement's month runs in the organisation's time zone (`timeZone`);
 * cost codes come from the project (`Project.costCode`), else its business area (`BusinessArea.costCode`);
 * an area manager reads `GET /admin/v1/costs?areaId=` and the chargeback lines of the areas they manage.
 *
 * What each dollar is (cost round 1, AIOE 0.16.0). `usd` is metered spend only: calls a provider
 * bills per token. Calls on a paid seat (`billing: "subscription"`) are not spend: what they would
 * have cost at list price is `subscriptionUsd`. Calls with no known price are counted in
 * `unpricedCalls` and are in no money figure. Calls to a model on the machine are `localCalls`.
 * Tokens and calls count every call.
 *
 * Cost round 3 (every addition optional; the console reads them, a workbench need not):
 *
 *   GET /admin/v1/costs/anomalies?since=
 *       administrators and portfolio managers (area managers: their areas): runs, tasks and person-days whose
 *       metered spend is more than `ANOMALY_MULTIPLE` times the median of their peers (same team and model,
 *       the trailing `ANOMALY_TRAILING_DAYS` days, at least `ANOMALY_MIN_PEERS` peers); each is flagged once,
 *       told to the project's owners and audited `cost.anomaly`
 *   GET /admin/v1/costs/outcomes?since=&until=&by=task|plan|pullRequest|roadmap&projectId=&areaId=
 *       cost per outcome, from the usage ledger only: the records carrying a board task (`taskId`, with its
 *       repository and team) joined to the board's tasks, the plans whose steps name them, the pull requests
 *       that name them, and the roadmap items the project's runs say the work served
 *
 * The chargeback statement gains the organisation's subscription seats (pricing.ts `seats`) as a fixed
 * cost, `seatUsd`, apart from metered spend: each vendor's monthly seats are shared across business
 * areas and projects by their share of that vendor's subscription-billed calls in the month; with no
 * such calls they are unallocated.
 *
 * Token economics (AIOE 0.90.0, every addition optional; the workbench's D401 usage fields, platform.ts):
 *
 *   GET /admin/v1/costs?…&by=member|session|repository|source
 *       four more groupings: the agent that made each call (a team's member, `agent:<teamId>/<name>`, or a
 *       specialist, `specialist:<id>`), the job it was made for (a workflow run, else the agent session, else the
 *       run: `costJobKey`), the repository (`<projectId>:<rootId>`) and the source (the runtime: Claude Code,
 *       Copilot, Codex, a cloud agent…). The rollups do not keep these, so they are read from the usage records
 *       themselves (summed in the database, never row by row) and reach back only as far as the organisation keeps
 *       records (`usageRetentionDays`); `CostRollup.records` says when a period reaches past that.
 *       Filters on every grouping: `teamId` (a team or a specialist), `rootId` (a repository, with `projectId`),
 *       `model`, beside `projectId`, `areaId` and `billing`. A `rootId` filter reads the records too.
 *       Lines carry what names them for people (`CostRank`: the agent's team, the session's agent, project,
 *       machine, start and end), so no surface shows a raw id.
 *   GET /admin/v1/costs/economics?since=&until=&bucket=&billing=&projectId=&areaId=&teamId=&rootId=&model=
 *       the overview on one answer (`CostEconomics`): spend at a glance (spend, tokens, calls, cache efficiency),
 *       usage and spend over time, how it is allocated by team, by source and by kind of work, and the highest
 *       spend named (agents, jobs, people). Same readers and the same rules as the costs.
 *
 * Totals carry `cacheReadTokens` and `cacheWriteTokens` (inside `promptTokens`, as platform.ts says); cache
 * efficiency is cache reads over prompt tokens (`cacheEfficiency`). On Mojo Up AI Cloud the two reads above are
 * served too; chargeback, invoices, unusual spend and cost per outcome stay the Enterprise edition's.
 *
 * Words for people: spend, subscription value, unpriced calls, tokens, calls, business area, project, team, person, model, machine,
 * unusual spend, seats, outcome, agent, session, job, repository, source, cache efficiency.
 */
import { zonedMidnight } from "./budgets.js";
import { z } from "zod";
import { monthYear } from "../wording/dateTime.js";
export const costGroupingSchema = z.enum(["area", "project", "team", "person", "model", "device", "member", "session", "repository", "source"]);
/** The groupings read from the usage records rather than the daily rollups (token economics, 0.90.0). */
export const COST_RECORD_GROUPINGS = ["member", "session", "repository", "source"];
export const costBucketSchema = z.enum(["day", "week", "month"]);
/** Which calls a costs query counts: metered (per-token) calls, calls on paid seats, or all of them. `usd` is always metered spend. */
export const costBillingFilterSchema = z.enum(["metered", "subscription", "all"]);
export const costsQuerySchema = z.object({
    since: z.string().optional(),
    until: z.string().optional(),
    by: costGroupingSchema.default("area"),
    bucket: costBucketSchema.optional(),
    /** Narrow to one business area's projects, or one project. */
    areaId: z.string().optional(),
    projectId: z.string().optional(),
    /** How many groups to name; the rest fold into one "Everything else" line. */
    top: z.coerce.number().int().min(1).max(200).default(25),
    billing: costBillingFilterSchema.default("all"),
    /** Token economics (0.90.0): one team or specialist, one repository (`rootId`, best with its `projectId`), one model. */
    teamId: z.string().max(200).optional(),
    rootId: z.string().max(200).optional(),
    model: z.string().max(200).optional(),
});
/** `GET /admin/v1/costs/economics`: the period, the calls counted and the filters of the costs. */
export const costEconomicsQuerySchema = costsQuerySchema.omit({ by: true, top: true });
/** Cache reads over prompt tokens, 0 to 1 to three places; null with no prompt tokens. */
export function cacheEfficiency(t) {
    if (!t.promptTokens)
        return null;
    return Math.round(Math.min(1, (t.cacheReadTokens ?? 0) / t.promptTokens) * 1000) / 1000;
}
/** What made a call, in words: the runtime a workbench names (`UsageRecord.runtime`). */
export const COST_SOURCE_WORDS = {
    "claude-code": "Claude Code",
    copilot: "GitHub Copilot",
    codex: "Codex",
    acp: "Agent client protocol",
    proxy: "Workbench model proxy",
    cloud: "Cloud agents",
    workbench: "AI Workbench",
    node: "Nodes",
};
export const costSourceWords = (runtime) => (runtime ? (COST_SOURCE_WORDS[runtime] ?? runtime) : "Not recorded");
/** The kinds of work a call is made for, most specific first (`costWorkKind`). */
export const costWorkKindSchema = z.enum(["workflow", "task", "run", "other"]);
export const COST_WORK_WORDS = { workflow: "Workflow runs", task: "Board tasks", run: "Agent runs", other: "Conversations and other work" };
/** The kind of work a usage record was made for: a workflow run, else a board task, else an agent run, else other. */
export function costWorkKind(r) {
    return r.workflowRunId ? "workflow" : r.taskId ? "task" : r.runId ? "run" : "other";
}
/** The job a usage record belongs to: `workflow:<id>` (one workflow run, all its sessions), else `session:<id>`, else `run:<id>`; "" when none. */
export function costJobKey(r) {
    return r.workflowRunId ? `workflow:${r.workflowRunId}` : r.sessionId ? `session:${r.sessionId}` : r.runId ? `run:${r.runId}` : "";
}
export const chargebackQuerySchema = z.object({
    /** YYYY-MM, UTC; the current month when left out. */
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
    format: z.enum(["json", "csv"]).default("json"),
});
/** `POST /admin/v1/chargeback/close`. */
export const chargebackCloseSchema = z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/) });
/** The bounds of a YYYY-MM month, in a time zone (UTC when not given). */
export function monthBounds(month, timeZone = "UTC") {
    const [y, m] = month.split("-").map(Number);
    return { since: new Date(zonedMidnight(timeZone, y, m - 1, 1)).toISOString(), until: new Date(zonedMidnight(timeZone, y, m, 1)).toISOString() };
}
/** The start of the bucket a time falls in, UTC. Weeks start on Monday. */
export function bucketStart(bucket, at) {
    const y = at.getUTCFullYear();
    const m = at.getUTCMonth();
    const d = at.getUTCDate();
    if (bucket === "month")
        return new Date(Date.UTC(y, m, 1)).toISOString();
    if (bucket === "week")
        return new Date(Date.UTC(y, m, d - ((at.getUTCDay() + 6) % 7))).toISOString();
    return new Date(Date.UTC(y, m, d)).toISOString();
}
/** A bucket that reads well for the length of the period: days up to six weeks, weeks up to a year, months beyond. */
export function bucketFor(sinceIso, untilIso) {
    const days = (Date.parse(untilIso) - Date.parse(sinceIso)) / 86_400_000;
    return days <= 42 ? "day" : days <= 370 ? "week" : "month";
}
/**
 * The statement as CSV: one row per project under its area, then one total row per area, then the grand total.
 * `usd` is metered spend; `subscription_usd` is the list-price value of calls on paid seats (not spend);
 * `unpriced_calls` counts calls with no known price, which are in no money column.
 */
export function chargebackCsv(s) {
    const cell = (v) => (typeof v === "number" ? String(v) : /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const row = (...cells) => cells.map(cell).join(",");
    // The organisation's currency, when it is not the US dollar, comes as one more column beside `usd`.
    const m = s.money && s.money.source !== "usd" && s.money.source !== "none" ? s.money : undefined;
    const local = (usd) => (m ? [(usd * m.perUsd).toFixed(6)] : []);
    // The seats' share, when the statement carries seats, comes as a last column (cost round 3).
    const seats = s.seats?.length ? true : false;
    const seat = (v) => (seats ? [(v ?? 0).toFixed(6)] : []);
    const figures = (t) => [t.usd.toFixed(6), ...local(t.usd), (t.subscriptionUsd ?? 0).toFixed(6), t.promptTokens, t.completionTokens, t.calls, t.unpricedCalls ?? 0];
    const out = [row("month", "area_id", "area", "cost_code", "project_id", "project", "kind", "usd", ...(m ? [`${m.currency.toLowerCase()} (1 usd = ${m.perUsd} ${m.currency}${m.asOf ? `, ${m.asOf}` : ""})`] : []), "subscription_usd", "prompt_tokens", "completion_tokens", "calls", "unpriced_calls", ...(seats ? ["seat_usd"] : []))];
    for (const l of s.lines) {
        for (const p of l.projects)
            out.push(row(s.month, l.areaId, l.name, p.costCode ?? l.costCode ?? "", p.projectId, p.name, p.kind, ...figures(p), ...seat(p.seatUsd)));
        out.push(row(s.month, l.areaId, l.name, l.costCode ?? "", "", "(area total)", "", ...figures(l), ...seat(l.seatUsd)));
    }
    out.push(row(s.month, "", "(total)", "", "", "", "", ...figures(s.total), ...seat(s.seatUsd)));
    // Late usage for closed months: one row per project, under the month it belongs to.
    for (const a of s.lateAdjustments ?? []) {
        for (const l of a.lines)
            for (const p of l.projects)
                out.push(row(`${a.month} (late)`, l.areaId, l.name, p.costCode ?? l.costCode ?? "", p.projectId, p.name, p.kind, ...figures(p), ...seat(0)));
        out.push(row(`${a.month} (late)`, "", "(late total)", "", "", "", "", ...figures(a.total), ...seat(0)));
    }
    return out.join("\n") + "\n";
}
// ── Cost round 3: unusual spend and cost per outcome ───────────────────────
/** A run, task or person-day is unusual past this many times the median of its peers. */
export const ANOMALY_MULTIPLE = 8;
/** At least this many peers (same team and model) before anything is called unusual. */
export const ANOMALY_MIN_PEERS = 5;
/** The peers are the trailing this many days. */
export const ANOMALY_TRAILING_DAYS = 14;
export const costAnomalyKindSchema = z.enum(["run", "task", "person-day"]);
export const costAnomaliesQuerySchema = z.object({
    /** ISO; the last 30 days when left out. */
    since: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
const SMALL = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen", "twenty"];
/** "nine times", "23 times". */
export function timesWords(times) {
    const n = Math.round(times);
    return `${n <= 20 ? SMALL[n] : String(n)} times`;
}
/** The comparison in words: "Run 3f2a cost US$42.10, nine times the median for this team and model". `money` formats US dollars for the reader. */
export function anomalyWords(a, money) {
    return `${a.subject} cost ${money(a.usd)}, ${timesWords(a.times)} the median for this team and model`;
}
export const costOutcomeGroupingSchema = z.enum(["task", "plan", "pullRequest", "roadmap"]);
export const costOutcomesQuerySchema = z.object({
    since: z.string().optional(),
    until: z.string().optional(),
    by: costOutcomeGroupingSchema.default("task"),
    projectId: z.string().optional(),
    areaId: z.string().optional(),
    top: z.coerce.number().int().min(1).max(1000).default(200),
});
// ── Cost round 4: invoice reconciliation and scheduled statements ──────────
//
//   POST   /admin/v1/invoices                      administrators: a provider's billing export for a month
//                                                  (multipart `file`, JSON `{ csv, fileName?, month?, provider? }`
//                                                  or text/csv; at most INVOICE_MAX_BYTES) → InvoiceUploadResponse
//   GET    /admin/v1/invoices?month=YYYY-MM         administrators: the invoices with their reconciliation, newest first
//   GET    /admin/v1/invoices/:id                   one, with its lines and reconciliation
//   DELETE /admin/v1/invoices/:id                   administrators; audited `invoice.delete`
//   POST   /admin/v1/chargeback/send { month, recipients?, format? }
//                                                  administrators: send a month's statement now → ChargebackSend
//   GET    /admin/v1/chargeback/sends?month=&limit= administrators: the statements sent, newest first
//
// `PUT /admin/v1/organisation { chargebackSchedule: ChargebackSchedule | null }` sets the schedule;
// `GET /people/v1/me/organisation` answers it to administrators only. A closed month's statement
// carries `invoices` (ChargebackInvoiceLine). Every addition is optional for older readers.
/** The largest billing export taken, in bytes of CSV text. */
export const INVOICE_MAX_BYTES = 5 * 1024 * 1024;
/** An invoice within this many per cent of the ledger is fine. */
export const INVOICE_FINE_PERCENT = 2;
/** The exports recognised from their header: Azure Cost Management, Anthropic's cost export, OpenAI's cost export, and `provider,model,month,amount,currency`. */
export const invoiceFormatSchema = z.enum(["azure", "anthropic", "openai", "generic"]);
export const INVOICE_FORMAT_WORDS = {
    azure: "Azure Cost Management export",
    anthropic: "Anthropic cost export",
    openai: "OpenAI cost export",
    generic: "Provider, model, month, amount",
};
/** The provider an invoice bills for, in words: azure-openai, anthropic, openai, or as a generic file names it. */
export const INVOICE_PROVIDER_WORDS = { "azure-openai": "Azure OpenAI", anthropic: "Anthropic", openai: "OpenAI", google: "Google", "github-copilot": "GitHub Copilot" };
export const invoiceProviderWords = (p) => INVOICE_PROVIDER_WORDS[p] ?? p;
/**
 * The provider an invoice or a ledger record bills under: Azure's OpenAI models are billed by Azure, so
 * they are their own (`azure-openai`); every other name folds as the price list folds it (pricing.ts vendorOf).
 */
export function invoiceProviderOf(provider) {
    const p = provider.trim().toLowerCase();
    if (p === "azure" || p.startsWith("azure-") || p.startsWith("azure ") || p === "azureopenai")
        return "azure-openai";
    const folded = { claude: "anthropic", "anthropic-api": "anthropic", "openai-codex": "openai", codex: "openai", gemini: "google", "google-ai": "google", vertex: "google", "vertex-ai": "google", copilot: "github-copilot", github: "github-copilot", "github-models": "github-copilot" };
    return folded[p] ?? p;
}
/** `fine` within INVOICE_FINE_PERCENT; `over`: the invoice is over the ledger; `under`: under it. */
export const invoiceVarianceSchema = z.enum(["fine", "over", "under"]);
/** `POST /admin/v1/invoices` as JSON (multipart carries the same fields beside `file`). */
export const invoiceUploadSchema = z.object({
    csv: z.string().min(1),
    fileName: z.string().max(300).optional(),
    /** The month, when the export spans several or carries no dates. */
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
    /** The provider, when a generic file or an Azure export should be read as another. */
    provider: z.string().min(1).max(100).optional(),
});
export const invoiceListQuerySchema = z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional() });
/** The cells of a CSV's first line, split as CSV (quotes honoured), lower case and trimmed. */
export function csvHeader(text) {
    const first = text.replace(/^﻿/, "").split(/\r?\n/, 1)[0] ?? "";
    const cells = [];
    let cell = "";
    let quoted = false;
    for (let i = 0; i < first.length; i++) {
        const c = first[i];
        if (quoted) {
            if (c === '"' && first[i + 1] === '"') {
                cell += '"';
                i++;
            }
            else if (c === '"')
                quoted = false;
            else
                cell += c;
        }
        else if (c === '"')
            quoted = true;
        else if (c === ",") {
            cells.push(cell);
            cell = "";
        }
        else
            cell += c;
    }
    cells.push(cell);
    return cells.map((c) => c.trim().toLowerCase());
}
/** Which export a header is (csvHeader's cells); undefined when none is recognised. */
export function detectInvoiceFormat(header) {
    const has = (...names) => names.some((n) => header.includes(n));
    if (has("provider") && has("model") && has("month") && has("amount"))
        return "generic";
    if (has("metername", "metercategory", "metersubcategory", "meter name", "meter category") && has("costinbillingcurrency", "costinusd", "pretaxcost", "cost", "cost in billing currency"))
        return "azure";
    if (has("amount_value", "line_item", "amount.value"))
        return "openai";
    const model = has("model", "model_name");
    const cost = header.some((h) => /cost|amount|usd/.test(h));
    if (model && cost && has("workspace", "workspace_id", "workspace_name", "api_key", "api_key_id", "usage_date_utc", "token_type", "service_tier", "cost_type", "context_window", "description"))
        return "anthropic";
    return undefined;
}
/** "October 2026" for 2026-10. */
export function monthWords(month) {
    return monthYear(`${month}-01T00:00:00Z`, { timeZone: "UTC" });
}
/** The variance of an invoiced figure from the ledger's: within INVOICE_FINE_PERCENT is fine. */
export function invoiceVariance(invoicedUsd, ledgerUsd) {
    const varianceUsd = Math.round((invoicedUsd - ledgerUsd) * 1_000_000) / 1_000_000;
    const base = invoicedUsd > 0 ? invoicedUsd : ledgerUsd;
    const variancePercent = base > 0 ? Math.round((Math.abs(varianceUsd) / base) * 1000) / 10 : 0;
    // A cent either way is fine whatever the percentage.
    const status = Math.abs(varianceUsd) < 0.01 || variancePercent <= INVOICE_FINE_PERCENT ? "fine" : varianceUsd > 0 ? "over" : "under";
    return { varianceUsd, variancePercent, status };
}
/**
 * A reconciliation in words, `money` formatting US dollars for the reader: "Anthropic invoiced US$1,204.10
 * for October 2026; the ledger has US$1,187.55 (1.4% under): claude-sonnet-5-5 US$12.30 under, unpriced calls 14".
 * Over and under are the ledger's, beside the invoice.
 */
export function reconciliationWords(r, money) {
    const head = `${invoiceProviderWords(r.provider)} invoiced ${money(r.invoicedUsd)} for ${monthWords(r.month)}; the ledger has ${money(r.ledgerUsd)}`;
    const side = (v) => (v > 0 ? "under" : "over");
    const overall = Math.abs(r.varianceUsd) < 0.005 ? " (the same)" : ` (${r.variancePercent}% ${side(r.varianceUsd)}${r.status === "fine" ? ", within " + INVOICE_FINE_PERCENT + "%" : ""})`;
    const off = r.models.filter((m) => m.status !== "fine").slice(0, 3).map((m) => `${m.model} ${money(Math.abs(m.varianceUsd))} ${m.ledgerUsd === 0 ? "not in the ledger" : m.invoicedUsd === 0 ? "not invoiced" : side(m.varianceUsd)}`);
    const more = r.models.filter((m) => m.status !== "fine").length - off.length;
    if (more > 0)
        off.push(`${more} more ${more === 1 ? "model" : "models"}`);
    if (r.unpricedCalls)
        off.push(`unpriced calls ${r.unpricedCalls.toLocaleString("en-AU")}`);
    return `${head}${overall}${off.length ? `: ${off.join(", ")}` : ""}`;
}
/** `chargeback.schedule`: the statement sent each month on `dayOfMonth` (the organisation's time zone) to `recipients`. */
export const chargebackScheduleSchema = z.object({
    dayOfMonth: z.number().int().min(1).max(10),
    recipients: z.array(z.email().max(320)).min(1).max(20),
    format: z.enum(["csv", "json"]),
    /** Close the month first when nobody has. */
    closeFirst: z.boolean(),
});
/** `POST /admin/v1/chargeback/send`: the schedule's recipients and format unless given. */
export const chargebackSendSchema = z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    recipients: z.array(z.email().max(320)).min(1).max(20).optional(),
    format: z.enum(["csv", "json"]).optional(),
});
//# sourceMappingURL=costs.js.map